import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError, formatUAH, formatNum } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Calendar, Folder, ChevronRight, Users, Banknote, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { planned: "Заплановано", in_progress: "В роботі", completed: "Завершено" };
const STATUS_COLOR = {
  planned: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-sky-50 text-sky-700 border-sky-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const BRANCH_OPTIONS = [
  { key: "phaco", label: "ФЕК" },
  { key: "anti_vegf", label: "Anti-VEGF (АЙЛІЯ)" },
  { key: "surgery", label: "Хірургічні операції" },
  { key: "laser", label: "Лазерні операції" },
  { key: "phaco_external", label: "ФЕК (зовнішні)" },
];
const BRANCH_LABEL = Object.fromEntries(BRANCH_OPTIONS.map((b) => [b.key, b.label]));
const BRANCH_COLOR = {
  phaco: "bg-teal-50 text-teal-700 border-teal-200",
  anti_vegf: "bg-indigo-50 text-indigo-700 border-indigo-200",
  surgery: "bg-rose-50 text-rose-700 border-rose-200",
  laser: "bg-amber-50 text-amber-700 border-amber-200",
  phaco_external: "bg-slate-100 text-slate-700 border-slate-200",
};

const WEEKDAYS = ["Неділя", "Понеділок", "Вівторок", "Середа", "Четвер", "П'ятниця", "Субота"];

function weekday(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return WEEKDAYS[d.getDay()];
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}.${m}.${y}`;
}

export default function OperationalDays() {
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", status: "", surgeon_id: "", branch: "" });
  const [open, setOpen] = useState(false);
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    // Iteration 30f: employees is a soft dependency (only for surgeon dropdown);
    // if the role lacks employees.read, degrade gracefully without a red toast.
    const [dRes, eRes] = await Promise.allSettled([
      api.get(`/operation-days?${params}`),
      api.get("/employees"),
    ]);
    if (dRes.status === "fulfilled") setDays(dRes.value.data);
    else toast.error(formatApiError(dRes.reason?.response?.data?.detail));
    if (eRes.status === "fulfilled") setEmployees(eRes.value.data);
    else setEmployees([]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const [newSurgeons, setNewSurgeons] = useState([]);
  const [newBranch, setNewBranch] = useState("phaco");

  // Iteration 34: any employee flagged as can_operate can be picked. External
  // surgeons only appear when the "phaco_external" branch is selected; for any
  // other branch the picker shows internal surgeons.
  const eligibleSurgeons = employees.filter((e) => {
    const canOp = e.can_operate !== undefined ? !!e.can_operate : e.role === "surgeon";
    if (!canOp) return false;
    if (newBranch === "phaco_external") return !!e.is_external_surgeon;
    return !e.is_external_surgeon;
  });
  // Filter dropdown surgeons — show ALL who can operate (both int + ext) so admins
  // can filter the day list regardless of the branch.
  const surgeons = employees.filter((e) => e.can_operate !== undefined ? !!e.can_operate : e.role === "surgeon");

  const toggleNewSurgeon = (id) => {
    setNewSurgeons((curr) => curr.includes(id)
      ? curr.filter((x) => x !== id)
      : (curr.length >= 3 ? (toast.error("Максимум 3 хірурги") || curr) : [...curr, id]));
  };

  const totals = days.reduce((acc, d) => ({
    ops: acc.ops + (d.summary?.operations_count || 0),
    revenue: acc.revenue + (d.summary?.total_amount || 0),
    debt: acc.debt + (d.summary?.debt_amount || 0),
  }), { ops: 0, revenue: 0, debt: 0 });

  const createDay = async () => {
    if (newSurgeons.length === 0) { toast.error("Оберіть щонайменше одного хірурга"); return; }
    try {
      const r = await api.post("/operation-days", { date: newDate, surgeons: newSurgeons, branch: newBranch });
      toast.success(`Операційний день ${formatDate(newDate)} створено`);
      setOpen(false);
      setNewSurgeons([]);
      setNewBranch("phaco");
      navigate(`/operational-days/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="operational-days-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Операційні дні</h1>
          <p className="text-sm text-slate-500">{days.length} днів · {formatNum(totals.ops)} операцій</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="create-day" className="bg-[#0F2027] hover:bg-[#0F2027]/90">
          <Plus className="h-4 w-4 mr-2" /> Створити операційний день
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="gv-card"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Усього операцій</div>
          <div className="gv-kpi-value">{formatNum(totals.ops)}</div>
        </CardContent></Card>
        <Card className="gv-card"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Загальна виручка</div>
          <div className="gv-kpi-value text-[#0D9488]">{formatUAH(totals.revenue)}</div>
        </CardContent></Card>
        <Card className="gv-card"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Загальний борг</div>
          <div className="gv-kpi-value text-red-600">{formatUAH(totals.debt)}</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card className="gv-card"><CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <div><Label className="text-xs">Від</Label>
          <Input type="date" data-testid="filter-from" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} />
        </div>
        <div><Label className="text-xs">До</Label>
          <Input type="date" data-testid="filter-to" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} />
        </div>
        <div><Label className="text-xs">Статус дня</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі</SelectItem>
              <SelectItem value="planned">Заплановано</SelectItem>
              <SelectItem value="in_progress">В роботі</SelectItem>
              <SelectItem value="completed">Завершено</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Гілка</Label>
          <Select value={filters.branch || "all"} onValueChange={(v) => setFilters({ ...filters, branch: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі гілки</SelectItem>
              {BRANCH_OPTIONS.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Хірург</Label>
          <Select value={filters.surgeon_id || "all"} onValueChange={(v) => setFilters({ ...filters, surgeon_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-surgeon"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі</SelectItem>
              {surgeons.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} data-testid="apply-filters" className="bg-[#0F2027] hover:bg-[#0F2027]/90">Застосувати</Button>
      </CardContent></Card>

      {/* Day cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {days.map((d) => (
          <Link
            key={d.id}
            to={`/operational-days/${d.id}`}
            data-testid={`day-card-${d.id}`}
            className="cursor-pointer group block no-underline"
          >
            <Card className="gv-card transition-all hover:border-[#0D9488] hover:-translate-y-0.5 hover:shadow-lg">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-slate-100 group-hover:bg-teal-50 flex items-center justify-center transition-colors">
                      <Folder className="h-6 w-6 text-[#0D9488]" />
                    </div>
                    <div>
                      <div className="heading font-bold text-xl text-[#0F2027] leading-tight">{formatDate(d.date)}</div>
                      <div className="text-xs text-slate-500">{weekday(d.date)}</div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-[#0D9488] transition-colors" />
                </div>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Badge className={`${STATUS_COLOR[d.status]} border`}>{STATUS_LABEL[d.status]}</Badge>
                  <Badge className={`${BRANCH_COLOR[d.branch || "phaco"]} border`} data-testid="day-branch-badge">{BRANCH_LABEL[d.branch || "phaco"]}</Badge>
                </div>

                {(d.surgeon_names || []).length > 0 && (
                  <div className="mb-3 text-xs">
                    <span className="text-slate-500">Хірурги: </span>
                    <span className="font-medium text-[#0F2027]">{(d.surgeon_names || []).join(", ")}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <div>
                      <div className="text-xs text-slate-500">Операцій</div>
                      <div className="font-bold text-[#0F2027]">{d.summary.operations_count}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-slate-400" />
                    <div>
                      <div className="text-xs text-slate-500">Хірургів</div>
                      <div className="font-bold text-[#0F2027]">{d.summary.surgeons_count}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Banknote className="h-4 w-4 text-slate-400" />
                    <div>
                      <div className="text-xs text-slate-500">Сума</div>
                      <div className="font-bold text-[#0F2027] tabular-nums">{formatUAH(d.summary.total_amount)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertCircle className={`h-4 w-4 ${d.summary.debt_amount > 0 ? "text-red-500" : "text-slate-400"}`} />
                    <div>
                      <div className="text-xs text-slate-500">Борг</div>
                      <div className={`font-bold tabular-nums ${d.summary.debt_amount > 0 ? "text-red-600" : "text-[#0F2027]"}`}>
                        {formatUAH(d.summary.debt_amount)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Оплачено: <span className="font-semibold text-emerald-700">{formatUAH(d.summary.paid_amount)}</span></span>
                  {d.summary.estimated_profit !== undefined && (
                    <span className="text-slate-500" data-testid="day-card-profit">Прибуток: <span className="font-semibold text-[#0D9488]">{formatUAH(d.summary.estimated_profit)}</span></span>
                  )}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
        {days.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-12">Немає операційних днів за обраними фільтрами</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Створити операційний день</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Дата</Label>
              <Input type="date" data-testid="new-day-date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
            </div>
            <div>
              <Label>Гілка <span className="text-rose-500">*</span></Label>
              <Select value={newBranch} onValueChange={(v) => { setNewBranch(v); setNewSurgeons([]); }}>
                <SelectTrigger data-testid="new-day-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BRANCH_OPTIONS.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">ФЕК і зовнішні ФЕК передбачають вибір IOL. Anti-VEGF та лазерні — без IOL.</div>
            </div>
            <div>
              <Label>Хірурги <span className="text-rose-500">*</span> <span className="text-xs text-slate-500 font-normal">(1–3 особи)</span></Label>
              <div className="mt-2 border border-slate-200 rounded-md max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="surgeons-picker">
                {eligibleSurgeons.length === 0 && (
                  <div className="p-3 text-sm text-slate-500">
                    {newBranch === "phaco_external"
                      ? "Немає зовнішніх хірургів. Додайте у «Працівники» — «Може бути хірургом» + «Зовнішній хірург»."
                      : "Немає внутрішніх хірургів. Позначте когось як «Може бути хірургом» у «Працівники»."}
                  </div>
                )}
                {eligibleSurgeons.map((s) => {
                  const checked = newSurgeons.includes(s.id);
                  return (
                    <label key={s.id} className={`flex items-center gap-3 p-2.5 text-sm cursor-pointer hover:bg-slate-50 ${checked ? "bg-teal-50" : ""}`} data-testid={`surgeon-opt-${s.id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleNewSurgeon(s.id)} className="accent-[#0D9488]" />
                      <span className={checked ? "font-medium text-[#0F2027]" : "text-slate-700"}>{s.full_name}</span>
                      {s.is_external_surgeon && <Badge className="bg-slate-100 text-slate-700 text-xs">Зовн.</Badge>}
                    </label>
                  );
                })}
              </div>
              <div className="text-xs text-slate-500 mt-1">Обрано: <span className="font-medium">{newSurgeons.length}</span> / 3</div>
            </div>
            <p className="text-xs text-slate-500">Якщо день з цією датою вже існує — буде відкрито його.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={createDay} data-testid="save-day" disabled={newSurgeons.length === 0} className="bg-[#0F2027]">Створити</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
